
import React from 'react';
import { AdminGuard } from '@/components/cms/AdminGuard';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MetricsCards } from '@/components/admin/MetricsCards';
import { SubscriptionChart } from '@/components/admin/SubscriptionChart';
import { CustomerTable } from '@/components/admin/CustomerTable';
import { RevenueChart } from '@/components/admin/RevenueChart';
import { UserBehaviorInsights } from '@/components/admin/UserBehaviorInsights';
import { SEO } from '@/components/common/SEO';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Home, Timer, Users, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AdminDashboard() {
  const adminStatus = useAdminStatus();

  return (
    <AdminGuard>
      <SEO 
        title="Admin Dashboard"
        description="Customer analytics and subscription management dashboard"
      />
      <div className="min-h-screen bg-gray-50/50">
        {/* Navigation Header */}
        <header className="bg-white border-b sticky top-0 z-40">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm" className="gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    Back to App
                  </Button>
                </Link>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                  <Badge variant={adminStatus.role === 'super_admin' ? 'default' : 'secondary'}>
                    {adminStatus.role?.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
              </div>
              <nav className="flex items-center gap-2">
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Home className="h-4 w-4" />
                    Dashboard
                  </Button>
                </Link>
                <Link to="/timer">
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Timer className="h-4 w-4" />
                    Timer
                  </Button>
                </Link>
                <Link to="/projects">
                  <Button variant="ghost" size="sm" className="gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Projects
                  </Button>
                </Link>
                <Link to="/clients">
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Users className="h-4 w-4" />
                    Clients
                  </Button>
                </Link>
              </nav>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <p className="text-gray-600">Monitor your subscribers, revenue, and user behavior</p>
          </div>

          <MetricsCards />

          <Tabs defaultValue="overview" className="mt-8">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="customers">Customers</TabsTrigger>
              <TabsTrigger value="revenue">Revenue</TabsTrigger>
              <TabsTrigger value="behavior">Behavior</TabsTrigger>
              <TabsTrigger value="insights">Insights</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SubscriptionChart />
                <RevenueChart />
              </div>
            </TabsContent>

            <TabsContent value="customers">
              <CustomerTable />
            </TabsContent>

            <TabsContent value="revenue">
              <div className="grid grid-cols-1 gap-6">
                <RevenueChart detailed />
              </div>
            </TabsContent>

            <TabsContent value="behavior">
              <UserBehaviorInsights />
            </TabsContent>

            <TabsContent value="insights">
              <Card>
                <CardHeader>
                  <CardTitle>Business Insights</CardTitle>
                  <CardDescription>Key insights and recommendations for your business</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h3 className="font-semibold text-blue-900">Conversion Funnel</h3>
                      <p className="text-blue-700 text-sm mt-1">
                        Track how users move from signup to paid subscription
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h3 className="font-semibold text-green-900">Growth Opportunities</h3>
                      <p className="text-green-700 text-sm mt-1">
                        Identify users approaching freemium limits for targeted conversion
                      </p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <h3 className="font-semibold text-amber-900">Churn Prevention</h3>
                      <p className="text-amber-700 text-sm mt-1">
                        Monitor inactive users and engagement patterns
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AdminGuard>
  );
}
